import { Refresh, Launch } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
} from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import { practiceConnectApi } from "apis";
import { useToast } from "contexts/ToastContext";
import { UpdatePaymentInfoResponseDto } from "libs/cellera-sdk";
import React, { useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

import CustomTransferTable from "./CustomTransferTable";

const PracticeConfiguration: React.FC = () => {
  const { colors, getColor } = useCelleraTheme();
  const { showSuccess, showError } = useToast();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  // Account status query with refetch
  const {
    data: accountStatus,
    isLoading: isAccountLoading,
    refetch: refetchAccountStatus,
  } = useQuery({
    queryKey: ["account-status"],
    queryFn: async () => {
      const response = await practiceConnectApi.getStatus();
      return response;
    },
  });

  // Transfer history query with pagination
  const {
    data: transferHistoryData,
    isLoading: isTransfersLoading,
    refetch: refetchTransferHistory,
  } = useQuery({
    queryKey: ["transfer-history", currentPage, pageSize],
    queryFn: async () => {
      const response = await practiceConnectApi.getTransferHistory({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });
      return response;
    },
  });

  const handleStartOnboarding = async () => {
    setOnboardingLoading(true);
    try {
      const response = await practiceConnectApi.startOnboarding();
      if (response.onboardingUrl) {
        window.open(response.onboardingUrl, "_blank");
        showSuccess("Onboarding link generated successfully");
      }
    } catch (error) {
      console.error("Error starting onboarding:", error);
      showError("Failed to start onboarding");
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handleRefresh = () => {
    refetchAccountStatus();
    refetchTransferHistory();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const updatePaymentInfoMutation = useMutation({
    mutationFn: async () => {
      return await practiceConnectApi.updatePaymentInfo();
    },
    onSuccess: (data: UpdatePaymentInfoResponseDto) => {
      showSuccess("Payment info update link generated successfully");
      window.open(data.loginUrl, "_blank");
    },
    onError: (error) => {
      console.error("Error generating payment info update link:", error);
      showError("Failed to generate payment info update link");
    },
  });

  const handleUpdatePaymentInfo = async () => {
    updatePaymentInfoMutation.mutate();
  };

  const isPayoutEligible =
    accountStatus?.hasAccount &&
    accountStatus?.isOnboarded &&
    accountStatus?.canReceivePayouts &&
    accountStatus?.chargesEnabled;

  const styleSuccessChip = {
    backgroundColor: colors.success[100],
    color: colors.success[700],
    borderRadius: "2px",
    border: `1px solid ${colors.success[500]}`,
  };

  const styleErrorChip = {
    backgroundColor: colors.error[100],
    color: colors.error[700],
    borderRadius: "2px",
    border: `1px solid ${colors.error[500]}`,
  };

  const accountDetails = accountStatus
    ? [
        { label: "Account ID", value: accountStatus.accountId },
        {
          label: "Onboard",
          value: accountStatus.isOnboarded ? (
            <Chip
              label="COMPLETED"
              color="success"
              size="small"
              sx={{
                ...styleSuccessChip,
              }}
            />
          ) : (
            <Chip
              label="INCOMPLETE"
              color="error"
              size="small"
              sx={{
                ...styleErrorChip,
              }}
            />
          ),
        },
        {
          label: "Stripe Account",
          value: accountStatus.hasAccount ? (
            <Chip
              label="ACTIVE"
              color="success"
              size="small"
              sx={{ ...styleSuccessChip }}
            />
          ) : (
            <Chip
              label="INACTIVE"
              color="error"
              size="small"
              sx={{ ...styleErrorChip }}
            />
          ),
        },
        {
          label: "Payouts Receive",
          value: accountStatus.canReceivePayouts ? (
            <Chip
              label="ACTIVE"
              color="success"
              size="small"
              sx={{ ...styleSuccessChip }}
            />
          ) : (
            <Chip
              label="INACTIVE"
              color="error"
              size="small"
              sx={{ ...styleErrorChip }}
            />
          ),
        },
        {
          label: "Charges Enabled",
          value: accountStatus.chargesEnabled ? (
            <Chip
              label="ACTIVE"
              color="success"
              size="small"
              sx={{ ...styleSuccessChip }}
            />
          ) : (
            <Chip
              label="INACTIVE"
              color="error"
              size="small"
              sx={{ ...styleErrorChip }}
            />
          ),
        },
      ]
    : [];

  if (isAccountLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Account Status Section */}
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            Wallet
          </Typography>
          <Tooltip title="Refresh">
            <Button
              startIcon={<Refresh />}
              onClick={handleRefresh}
              sx={{ minWidth: "auto" }}
            >
              Refresh
            </Button>
          </Tooltip>
        </Box>

        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            {accountStatus ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 0 },
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {accountDetails.map((detail, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 180 }}
                      >
                        {detail.label}:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {detail.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {isPayoutEligible ? (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column" },
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleUpdatePaymentInfo}
                      disabled={updatePaymentInfoMutation.isPending}
                      sx={{
                        borderColor: getColor("primary.main"),
                        color: "white",
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        borderRadius: 1,
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: getColor("primary.dark"),
                          borderColor: getColor("primary.dark"),
                        },
                      }}
                    >
                      {updatePaymentInfoMutation.isPending
                        ? "Generating link..."
                        : "Update Payment Info"}
                    </Button>
                  </Box>
                ) : !accountStatus.isOnboarded ? (
                  <Button
                    variant="contained"
                    onClick={handleStartOnboarding}
                    disabled={onboardingLoading}
                    startIcon={
                      onboardingLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Launch />
                      )
                    }
                    sx={{
                      backgroundColor: getColor("primary.main"),
                      color: "white",
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      borderRadius: 1,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: getColor("primary.dark"),
                      },
                    }}
                  >
                    {onboardingLoading ? "Processing..." : "Start Onboarding"}
                  </Button>
                ) : null}
              </Box>
            ) : (
              <Alert severity="error">
                Unable to load account status. Please try refreshing the page.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Transfer History Section */}
      <CustomTransferTable
        transfers={transferHistoryData?.transfers || []}
        isLoading={isTransfersLoading}
        currentPage={currentPage}
        totalCount={transferHistoryData?.total || 0}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        pageSize={pageSize}
      />
    </Box>
  );
};

export default PracticeConfiguration;
